// v1 prompt template seed (D-22, D-28). Placeholder syntax is `{{a.b.c}}`
// (D-21), rendered by `promptAssembly.ts` (Task 1.1) against the
// `PromptContext` built from a project/item/deliverable. These are global
// templates (no `projectId`); a project may still override one by key via
// `putTemplateIfAbsent`-style rows under its own PK (Task 1.2).

import type { ContentType, PromptTemplate } from "./types.js";

const SEEDED_AT = "2026-09-04T00:00:00.000Z";

const DEFAULT_VARIANT_HINTS = [
  "",
  "Use a different composition than the obvious one.",
  "Favor a simpler, more minimal treatment.",
  "Favor more detail and richer texture.",
];

function template(partial: Omit<PromptTemplate, "entityType" | "updatedAt" | "version" | "variantHints"> & {
  variantHints?: string[];
}): PromptTemplate {
  return {
    entityType: "PromptTemplate",
    updatedAt: SEEDED_AT,
    version: 1,
    variantHints: partial.variantHints ?? DEFAULT_VARIANT_HINTS,
    ...partial,
  };
}

const GYM_HOW_TO_SCHEMA = {
  type: "object",
  additionalProperties: false,
  required: ["name", "summary", "steps"],
  properties: {
    name: { type: "string" },
    summary: { type: "string" },
    steps: { type: "array", minItems: 3, maxItems: 8, items: { type: "string" } },
    formTips: { type: "array", minItems: 0, maxItems: 4, items: { type: "string" } },
    commonMistakes: { type: "array", minItems: 0, maxItems: 4, items: { type: "string" } },
    breathing: { type: "string" },
    unilateralNote: { type: "string" },
  },
};

// The picture-book outline's structured output (Picture-Book Workflow plan,
// Task 2.1). `pagesFromOutline` in `shared/src/expansion.ts` reads this shape;
// `maxItems: 60` is a long picture book that still fits one bulk-create request.
const PICTURE_BOOK_OUTLINE_SCHEMA = {
  type: "object",
  additionalProperties: false,
  required: ["title", "characters", "pages"],
  properties: {
    title: { type: "string" },
    characters: {
      type: "array",
      items: {
        type: "object",
        additionalProperties: false,
        required: ["name", "description"],
        properties: { name: { type: "string" }, description: { type: "string" } },
      },
    },
    pages: {
      type: "array",
      minItems: 1,
      maxItems: 60,
      items: {
        type: "object",
        additionalProperties: false,
        required: ["pageNumber", "scene", "text"],
        properties: {
          pageNumber: { type: "integer" },
          scene: { type: "string" },
          text: { type: "string" },
        },
      },
    },
  },
};

export const TEMPLATE_SEED: PromptTemplate[] = [
  template({
    templateId: "text/instructions",
    contentType: "text",
    name: "How-to instructions",
    system:
      "You write clear, safe, step-by-step how-to instructions for physical activities. " +
      "Respond with JSON only, matching the given schema exactly - no markdown fences, no prose outside the JSON.",
    user:
      "{{project.styleGuide}}\n{{project.sharedContext}}\n{{project.negativeGuidance}}\n\n" +
      "Title: {{item.title}}\nBrief: {{item.brief}}\n\n{{deliverable.instructions}}",
    outputSchema: GYM_HOW_TO_SCHEMA,
  }),
  template({
    templateId: "text/description",
    contentType: "text",
    name: "Free prose description",
    system: "You write engaging, accurate prose descriptions, 80-200 words, plain text only.",
    user:
      "{{project.styleGuide}}\n{{project.sharedContext}}\n{{project.negativeGuidance}}\n\n" +
      "Title: {{item.title}}\nBrief: {{item.brief}}\n\n{{deliverable.instructions}}",
  }),
  template({
    templateId: "svg/animation",
    contentType: "svg",
    name: "Animated demonstration",
    system:
      "You author a single self-contained animated SVG demonstrating the described action. " +
      "Rules: one root <svg> with a viewBox; include <title> and <desc>; the animation loops " +
      "over 3-6 seconds; wrap animated properties in @media (prefers-reduced-motion: reduce) " +
      "so motion freezes for users who ask for it; never use <script>, event attributes, " +
      "<foreignObject>, external href/url()/xlink:href, embedded raster images, <iframe>, " +
      "<embed>, or <object>. Respond with the SVG markup only - no markdown fences, no prose.",
    user:
      "{{project.styleGuide}}\n{{project.sharedContext}}\n{{project.negativeGuidance}}\n\n" +
      "Title: {{item.title}}\nBrief: {{item.brief}}\n\n{{deliverable.instructions}}\n\n{{feedback}}\n\n{{variant.hint}}",
  }),
  template({
    templateId: "svg/icon",
    contentType: "svg",
    name: "Flat icon",
    system:
      "You author a flat, single-color icon in a 24x24 viewBox with consistent stroke width " +
      "and a silhouette that reads clearly at small sizes. Same safety rules as any SVG output: " +
      "no <script>, event attributes, <foreignObject>, external references, or embedded raster. " +
      "Respond with the SVG markup only - no markdown fences, no prose.",
    user: "Icon for: {{item.title}}\n{{item.brief}}\n\n{{deliverable.instructions}}\n\n{{variant.hint}}",
  }),
  template({
    // Picture-Book Workflow plan, Task 2.1. `expansionKind` lets an accepted
    // outline be fanned out into one Item per page by
    // POST /api/deliverables/:id/expand.
    templateId: "text/picture-book-outline",
    contentType: "text",
    name: "Picture-book outline",
    expansionKind: "picture-book",
    system:
      "You are a children's picture-book author. Respond with JSON only, matching the given " +
      "schema exactly - no markdown fences, no prose outside the JSON. `scene` is a visual brief " +
      'for the illustrator; `text` is the exact words printed on that page (may be "" for a ' +
      "wordless page). Keep vocabulary and sentence length suited to the stated age range.",
    user:
      "{{project.styleGuide}}\n{{project.sharedContext}}\n\nPremise: {{item.brief}}\n\n" +
      "{{deliverable.instructions}}\n\n{{feedback}}",
    outputSchema: PICTURE_BOOK_OUTLINE_SCHEMA,
  }),
  template({
    // Picture-Book Workflow plan, Task 2.1. Chosen by the expansion mapper for
    // each page; injects the whole accepted outline via {{upstream.outline}}
    // for character + style continuity.
    templateId: "image/picture-book-page",
    contentType: "image",
    name: "Picture-book page",
    system:
      "You illustrate a single page of a children's picture book. Keep every recurring character " +
      "exactly on-model across pages, matching the character sheet and any reference images. Do " +
      "not draw the page's words as text unless the instructions ask for it.",
    user:
      "{{project.styleGuide}}\n{{project.sharedContext}}\n{{project.negativeGuidance}}\n\n" +
      "Story (for character and style continuity - do not render this text):\n{{upstream.outline}}\n\n" +
      "This page - {{item.title}}:\nScene: {{item.brief}}\nWords on this page (for context; do not " +
      "draw as text unless asked): {{item.attributes.pageText}}\n\n{{deliverable.instructions}}\n\n" +
      "{{feedback}}\n\n{{variant.hint}}",
  }),
  template({
    templateId: "image/illustration",
    contentType: "image",
    name: "Illustration page",
    system: "You generate a single illustration page consistent with the given style guide and character sheet.",
    user:
      "{{project.styleGuide}}\n{{project.sharedContext}}\n{{project.negativeGuidance}}\n\n" +
      "Page: {{item.title}}\n{{item.brief}}\n\n{{deliverable.instructions}}\n\n{{feedback}}\n\n{{variant.hint}}",
  }),
  template({
    // No LLM step: the audio adapter (Task 2.4) speaks `deliverable.instructions`
    // when set, else falls back to `item.brief` - a fallback `{{}}` placeholders
    // cannot express, so `user` here is only the adapter's fallback text.
    templateId: "audio/narration",
    contentType: "audio",
    name: "Narration",
    system: "",
    user: "{{item.brief}}",
  }),
  template({
    templateId: "video/clip",
    contentType: "video",
    name: "Short clip",
    system: "You generate a short (default 5 second) video clip consistent with the given style guide.",
    user:
      "{{project.styleGuide}}\n{{project.sharedContext}}\n{{project.negativeGuidance}}\n\n" +
      "{{item.title}}\n{{item.brief}}\n\n{{deliverable.instructions}}\n\n{{variant.hint}}",
  }),
];

/**
 * The template a deliverable of each content type resolves to when it carries
 * no explicit `templateId` (G-3). The server never depends on the client
 * sending one; the SPA reads this map only to preselect the dropdown. Every id
 * here must exist in `TEMPLATE_SEED` - `shared/test/templateSeed.test.js`
 * enforces that.
 */
export const DEFAULT_TEMPLATE_BY_CONTENT_TYPE: Record<ContentType, string> = {
  text: "text/instructions",
  svg: "svg/animation",
  image: "image/illustration",
  audio: "audio/narration",
  video: "video/clip",
};

export function defaultTemplateIdFor(contentType: ContentType): string {
  return DEFAULT_TEMPLATE_BY_CONTENT_TYPE[contentType];
}
